import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle} from 'views/components';
import {Dropdown, DropdownMenuItem, Typography} from 'views/components';
import {MarkdownEditor, Translate, translate} from 'views/module';

import PillDropdown from './PillDropdown';

const StageDropdown = (props) => {
  const {selectedStage, setSelectedStage, leadQuality} = props;

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isReasonDialogOpen, setIsReasonDialogOpen] = React.useState(false);
  const [pendingStage, setPendingStage] = React.useState(null);
  const [statusReason, setStatusReason] = React.useState('');
  const [presetReason, setPresetReason] = React.useState('');

  const statusRequiringReason = ['INACTIVE'];
  const statusRequiringPresetReason = ['CLOSED_LOST'];
  const stages = Object.entries(translate('client.Status'));
  const presetReasons = Object.entries(translate('client.ClosedLostReason'));
  const isPresetReason = statusRequiringPresetReason.includes(pendingStage);
  const presetReasonTitle = presetReason ? translate(`client.ClosedLostReason.${presetReason}.Title`) : '';
  const stageData = translate(`client.Status.${selectedStage}`);
  const pendingStageTitle = pendingStage ? translate(`client.Status.${pendingStage}.Title`) : '';
  const leadQualityColor = leadQuality && translate(`client.LeadQuality.${leadQuality}.Color`);
  const onOverlayChange = props.onOverlayChange;

  React.useEffect(() => {
    onOverlayChange?.(isMenuOpen || isReasonDialogOpen);
  }, [isMenuOpen, isReasonDialogOpen, onOverlayChange]);

  const closeReasonDialog = () => {
    setIsReasonDialogOpen(false);
    setPendingStage(null);
    setStatusReason('');
    setPresetReason('');
  };

  const handleOptionSelect = (nextStage) => {
    if (!nextStage || nextStage === selectedStage) {
      return;
    }

    if (statusRequiringReason.includes(nextStage) || statusRequiringPresetReason.includes(nextStage)) {
      setPendingStage(nextStage);
      setIsReasonDialogOpen(true);
      return;
    }

    setSelectedStage(nextStage);
  };

  const handleSaveReason = () => {
    const reason = isPresetReason ? presetReasonTitle : statusReason.trim();

    if (!pendingStage || !reason) {
      return;
    }

    setSelectedStage(pendingStage, {
      blob: reason,
      editorType: 'MARKDOWN',
    });
    closeReasonDialog();
  };

  return (
    <>
      <PillDropdown
        backgroundColor={stageData.Color || 'transparent'}
        borderColor={stageData.ColorBorder || stageData.Color || 'transparent'}
        label={
          <Box sx={{display: 'inline-flex', alignItems: 'center', gap: '6px'}}>
            {
              leadQualityColor &&
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: leadQualityColor,
                  flexShrink: 0,
                }}
              />
            }
            <Translate expression={`client.Status.${selectedStage}.Title`} />
          </Box>
        }
        menuLabel={translate('client.ClientDetail.Status.Menu.Subtitles.Stage')}
        onOpenChange={setIsMenuOpen}
        onOptionSelect={handleOptionSelect}
        options={stages}
        selectedValue={selectedStage}
        textColor={stageData.ColorText || '#000000'}
      />
      {
        isReasonDialogOpen &&
        <Dialog onClose={closeReasonDialog} open={isReasonDialogOpen}>
          <DialogTitle onClose={closeReasonDialog}>
            <Translate expression='client.ClientDetail.ChangeStatusDialog.ReasonTitle' />
          </DialogTitle>
          <DialogContent>
            <Box sx={{mt: 1, minWidth: {sm: '28rem'}}}>
              <Typography sx={{mb: 2}}>
                <Translate
                  expression='client.ClientDetail.ChangeStatusDialog.ReasonPrompt'
                  substitute={{status: pendingStageTitle}}
                />
              </Typography>
              {
                isPresetReason &&
                <Dropdown
                  onChange={setPresetReason}
                  value={presetReason}
                  label={translate('client.ClientDetail.ChangeStatusDialog.Reason')}
                >
                  {
                    presetReasons.map(([k, v]) => {
                      return (
                        <DropdownMenuItem key={k} value={k}>
                          {v.Title}
                        </DropdownMenuItem>
                      );
                    })
                  }
                </Dropdown>
              }
              {
                !isPresetReason &&
                <>
                  <Typography variant='body2' sx={{mb: 1}}>
                    {translate('client.ClientDetail.ChangeStatusDialog.Reason')}
                  </Typography>
                  <Typography color='text.secondary' variant='body2' sx={{mb: 1}}>
                    {translate('client.ClientDetail.ChangeStatusDialog.ReasonPlaceholder')}
                  </Typography>
                  <MarkdownEditor
                    autoFocus
                    height={220}
                    value={statusReason}
                    onChange={(value) => setStatusReason(value || '')}
                  />
                </>
              }
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={closeReasonDialog}>
              <Translate expression='generic.Cancel' />
            </Button>
            <Button
              color='primary'
              disabled={isPresetReason ? !presetReason : !statusReason.trim()}
              onClick={handleSaveReason}
            >
              <Translate expression='generic.Save' />
            </Button>
          </DialogActions>
        </Dialog>
      }
    </>
  );
};

StageDropdown.propTypes = {
  onOverlayChange: PropTypes.func,
  selectedStage: PropTypes.string.isRequired,
  setSelectedStage: PropTypes.func.isRequired,
  leadQuality: PropTypes.string,
};

export default StageDropdown;
